import { useId, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router'
import type { ListState } from '../transactions/useFilterOptions'
import type { LocalDate } from '../../platform/localDate'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { FilterDialog, type FilterChoice } from './FilterDialog'
import {
  activePreset,
  customError,
  presetWindow,
  PRESET_TITLES,
  type FilterNames,
  type PresetId,
  type ReportFilters,
  type ReportSpec,
  type RowsBy,
} from './reportSpec'
import './reports.css'

type Control = 'range' | 'columns' | 'filter' | 'rows'
type FilterField = 'accounts' | 'owners' | 'groups' | 'tags'

type Props = {
  spec: ReportSpec
  today: LocalDate
  /** Root categories, from `useCategories`. */
  groups: { id: string; name: string }[]
  groupsLoading?: boolean
  /** The transactions filter bar's own lists (`useFilterOptions`). */
  accounts: { id: string; label: string }[]
  owners: { id: string; label: string }[]
  tags: string[]
  /** Per-list readiness (`useFilterOptions().lists`). A list not named is ready. */
  lists?: Partial<Record<'accounts' | 'owners' | 'tags', ListState>>
  filterNames: FilterNames
  onChange: (next: ReportSpec, control: Control) => void
  /** A preset replaces the whole spec. */
  onPreset: (id: PresetId) => void
  /** The container owns the merchant dialog. */
  onPickMerchant: () => void
  /** `/cashflow`, or `/cashflow?granularity=quarter`. */
  incomeVsExpenseTo: string
}

const errorOf = (l: ListState | undefined) => (l?.status === 'error' ? { retry: l.retry } : null)

const RANGES = [
  { value: 'mtd', label: 'MTD' },
  { value: 'ytd', label: 'YTD' },
  { value: '12m', label: '12M' },
  { value: 'custom', label: 'Custom' },
] as const

const ROWS_BY = [
  { value: 'category', label: 'Category' },
  { value: 'group', label: 'Group' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'owner', label: 'Owner' },
  { value: 'account', label: 'Account' },
  { value: 'tag', label: 'Tag' },
] as const

/** One string for the option's screen-reader reason and the visible note. */
const OWNER_REASON = 'Owner columns need rows other than Owner.'
const COLUMNS_BY = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'owner', label: 'Owner' },
] as const

const FIELD_TITLE: Record<FilterField, string> = { accounts: 'Accounts', owners: 'Owners', groups: 'Category groups', tags: 'Tags' }

/**
 * Two editable dates. A draft is held while typing and committed on blur or Enter, and only when it is
 * valid (`customError`), so an inverted or over-long range never reaches the URL or the server (R5, R9).
 */
function CustomRange({ spec, today, onCommit }: { spec: ReportSpec; today: LocalDate; onCommit: (s: LocalDate, e: LocalDate) => void }) {
  const [start, setStart] = useState(spec.start)
  const [end, setEnd] = useState(spec.end)
  const error = customError(start, end, today)
  const commit = () => {
    if (error === null && (start !== spec.start || end !== spec.end)) onCommit(start, end)
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit()
  }
  return (
    <div className="rp-rail__dates">
      <Field label="From" type="date" max={today} value={start} onChange={(e) => setStart(e.target.value)} onBlur={commit} onKeyDown={onKey} />
      <Field
        label="To"
        type="date"
        max={today}
        value={end}
        error={error ?? undefined}
        onChange={(e) => setEnd(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
      />
    </div>
  )
}

type Chip = { key: string; label: string; remove: (f: ReportFilters) => ReportFilters }

/** One chip per value in a field, then its none option. A name still loading reads as the field's own word. */
function chipsOf(f: ReportFilters, names: FilterNames): { field: string; chips: Chip[] }[] {
  const without = (list: string[], v: string) => list.filter((x) => x !== v)
  const out: { field: string; chips: Chip[] }[] = []
  if (f.groups.length > 0 || f.uncategorised) {
    out.push({
      field: 'Category group',
      chips: [
        ...f.groups.map((id) => ({ key: id, label: names.group(id) ?? 'Category group', remove: (x: ReportFilters) => ({ ...x, groups: without(x.groups, id) }) })),
        ...(f.uncategorised ? [{ key: 'none', label: 'Uncategorised', remove: (x: ReportFilters) => ({ ...x, uncategorised: false }) }] : []),
      ],
    })
  }
  if (f.merchants.length > 0 || f.noMerchant) {
    out.push({
      field: 'Merchant',
      chips: [
        ...f.merchants.map((id) => ({ key: id, label: names.merchant(id) ?? 'Merchant', remove: (x: ReportFilters) => ({ ...x, merchants: without(x.merchants, id) }) })),
        ...(f.noMerchant ? [{ key: 'none', label: 'No merchant', remove: (x: ReportFilters) => ({ ...x, noMerchant: false }) }] : []),
      ],
    })
  }
  if (f.accounts.length > 0) {
    out.push({
      field: 'Account',
      chips: f.accounts.map((id) => ({ key: id, label: names.account(id) ?? 'Account', remove: (x: ReportFilters) => ({ ...x, accounts: without(x.accounts, id) }) })),
    })
  }
  if (f.owners.length > 0) {
    out.push({
      field: 'Owner',
      chips: f.owners.map((id) => ({ key: id, label: names.owner(id) ?? 'Owner', remove: (x: ReportFilters) => ({ ...x, owners: without(x.owners, id) }) })),
    })
  }
  if (f.tags.length > 0 || f.untagged) {
    out.push({
      field: 'Tag',
      chips: [
        ...f.tags.map((t) => ({ key: t, label: t, remove: (x: ReportFilters) => ({ ...x, tags: without(x.tags, t) }) })),
        ...(f.untagged ? [{ key: 'none', label: 'Untagged', remove: (x: ReportFilters) => ({ ...x, untagged: false }) }] : []),
      ],
    })
  }
  if (f.recurring) {
    out.push({ field: 'Recurring', chips: [{ key: 'recurring', label: 'Recurring merchants only', remove: (x) => ({ ...x, recurring: false }) }] })
  }
  return out
}

export function ReportRail({
  spec,
  today,
  groups,
  groupsLoading = false,
  accounts,
  owners,
  tags,
  lists = {},
  filterNames,
  onChange,
  onPreset,
  onPickMerchant,
  incomeVsExpenseTo,
}: Props) {
  const soonId = useId()
  const [menu, setMenu] = useState(false)
  const [dialog, setDialog] = useState<FilterField | null>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  const active = activePreset(spec)

  const setRange = (value: (typeof RANGES)[number]['value']) => {
    if (value === 'custom') onChange({ ...spec, range: 'custom' }, 'range')
    else onChange({ ...spec, range: value, ...presetWindow(value, today) }, 'range')
  }
  const setRows = (rows: RowsBy) => {
    onChange(
      {
        ...spec,
        rows,
        // The server refuses owner rows with owner columns, and a tag report cannot stack.
        columns: rows === 'owner' && spec.columns === 'owner' ? 'month' : spec.columns,
        chart: rows === 'tag' && spec.chart === 'stacked' ? 'grouped' : spec.chart,
      },
      'rows',
    )
  }
  const setFilters = (filters: ReportFilters) => onChange({ ...spec, filters }, 'filter')

  const f = spec.filters
  const choice = (field: FilterField): FilterChoice =>
    field === 'accounts'
      ? { ids: f.accounts, none: false }
      : field === 'owners'
        ? { ids: f.owners, none: false }
        : field === 'groups'
          ? { ids: f.groups, none: f.uncategorised }
          : { ids: f.tags, none: f.untagged }
  const apply = (field: FilterField, c: FilterChoice) => {
    const ids = [...new Set(c.ids)].sort()
    setDialog(null)
    setMenu(false)
    if (field === 'accounts') setFilters({ ...f, accounts: ids })
    else if (field === 'owners') setFilters({ ...f, owners: ids })
    else if (field === 'groups') setFilters({ ...f, groups: ids, uncategorised: c.none })
    else setFilters({ ...f, tags: ids, untagged: c.none })
  }
  const optionsFor = (field: FilterField): { id: string; label: string }[] =>
    field === 'accounts' ? accounts : field === 'owners' ? owners : field === 'groups' ? groups.map((g) => ({ id: g.id, label: g.name })) : tags.map((t) => ({ id: t, label: t }))

  const presets = (Object.keys(PRESET_TITLES) as PresetId[]).map((id) => ({ id, label: PRESET_TITLES[id] }))
  const groupsOfChips = chipsOf(f, filterNames)

  return (
    <Panel className="rp-rail" aria-label="Report builder">
      <section className="rp-rail__section" aria-labelledby={`${soonId}-reports`}>
        <h3 id={`${soonId}-reports`} className="rp-rail__title">
          Reports
        </h3>
        {presets.map((p) => (
          <button
            key={p.id}
            type="button"
            className="rp-rail__item"
            aria-current={active === p.id ? 'true' : undefined}
            onClick={() => onPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        <Link className="rp-rail__item" to={incomeVsExpenseTo}>
          Income vs expense
        </Link>
      </section>

      <section className="rp-rail__section" aria-labelledby={`${soonId}-range`}>
        <h3 id={`${soonId}-range`} className="rp-rail__title">
          Date range
        </h3>
        <SegmentedControl label="Date range" name="rp-range" options={RANGES} value={spec.range} onChange={setRange} tone="accent" />
        {spec.range === 'custom' ? (
          <CustomRange
            key={`${spec.start}|${spec.end}`}
            spec={spec}
            today={today}
            onCommit={(start, end) => onChange({ ...spec, range: 'custom', start, end }, 'range')}
          />
        ) : (
          <div className="rp-rail__dates">
            <Field label="From" type="date" value={spec.start} readOnly />
            <Field label="To" type="date" value={spec.end} readOnly />
          </div>
        )}
      </section>

      <section className="rp-rail__section" aria-labelledby={`${soonId}-rows`}>
        <h3 id={`${soonId}-rows`} className="rp-rail__title">
          Group rows by
        </h3>
        <SegmentedControl label="Group rows by" name="rp-rows" options={ROWS_BY} value={spec.rows} onChange={setRows} tone="accent" />
      </section>

      <section className="rp-rail__section" aria-labelledby={`${soonId}-cols`}>
        <h3 id={`${soonId}-cols`} className="rp-rail__title">
          Columns by
        </h3>
        <SegmentedControl
          label="Columns by"
          name="rp-columns"
          options={COLUMNS_BY.map((c) => (c.value === 'owner' && spec.rows === 'owner' ? { ...c, unavailable: OWNER_REASON } : c))}
          value={spec.columns}
          onChange={(v) => onChange({ ...spec, columns: v }, 'columns')}
          tone="accent"
        />
        {spec.rows === 'owner' && (
          <p className="note">
            {OWNER_REASON}
          </p>
        )}
      </section>

      <section className="rp-rail__section" aria-labelledby={`${soonId}-filters`}>
        <h3 id={`${soonId}-filters`} className="rp-rail__title">
          Filters
        </h3>
        <p className="rp-rail__fixed">Excludes transfers</p>
        {groupsOfChips.map(({ field, chips }) => (
          <div key={field} className="rp-rail__field" role="group" aria-label={field}>
            <span className="rp-rail__field-name">{field}</span>
            {chips.map((c) => (
              <span key={c.key} className="rp-rail__chip">
                <span>{c.label}</span>
                <button
                  type="button"
                  className="rp-rail__chip-remove"
                  aria-label={`Remove filter ${c.label}`}
                  onClick={() => setFilters(c.remove(f))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ))}
        <Button ref={addRef} variant="outline" size="panel" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
          ＋ Add filter
        </Button>
        {menu && (
          <div className="rp-rail__menu" role="group" aria-label="Add a filter">
            {(['accounts', 'owners', 'groups', 'tags'] as const).map((field) => (
              <Button key={field} variant="chip" size="panel" onClick={() => setDialog(field)}>
                {FIELD_TITLE[field]}…
              </Button>
            ))}
            <Button
              variant="chip"
              size="panel"
              onClick={() => {
                setMenu(false)
                onPickMerchant()
              }}
            >
              Merchant…
            </Button>
            {!f.noMerchant && (
              <Button
                variant="chip"
                size="panel"
                onClick={() => {
                  setMenu(false)
                  setFilters({ ...f, noMerchant: true })
                }}
              >
                No merchant
              </Button>
            )}
            {!f.recurring && (
              <Button
                variant="chip"
                size="panel"
                onClick={() => {
                  setMenu(false)
                  setFilters({ ...f, recurring: true })
                }}
              >
                Recurring merchants only
              </Button>
            )}
          </div>
        )}
      </section>

      {(['accounts', 'owners', 'groups', 'tags'] as const).map((field) => (
        <FilterDialog
          key={field}
          open={dialog === field}
          field={FIELD_TITLE[field]}
          options={optionsFor(field)}
          noneLabel={field === 'groups' ? 'Uncategorised' : field === 'tags' ? 'Untagged' : null}
          selected={choice(field)}
          loading={field === 'groups' ? groupsLoading : lists[field]?.status === 'loading'}
          failed={field === 'groups' ? null : errorOf(lists[field])}
          onApply={(c) => apply(field, c)}
          onClose={() => setDialog(null)}
          restoreFocusTo={addRef}
        />
      ))}
    </Panel>
  )
}
